import * as React from "react";
import { userApi } from "../../api";
import { useSearchParams } from "react-router";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SubmitHandler, useForm } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { isAxiosError } from "axios";
import { Loader2 } from "lucide-react";

enum PasswordResetErrors {
  EXPIRED_TOKEN = "EXPIRED_TOKEN",
  INVALID_TOKEN = "INVALID_TOKEN",
}

type Inputs = {
  password: string;
};

export default ({ success }: { success: VoidFunction }) => {
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [searchParams] = useSearchParams();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<Inputs>();

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    const token = searchParams.get("token");
    if (!token) {
      return;
    }

    setSubmitting(true);
    userApi
      .resetPassword(data.password, token)
      .then(() => {
        success();
      })
      .catch((err) => {
        if (isAxiosError(err)) {
          if (err.response?.data.error === PasswordResetErrors.EXPIRED_TOKEN) {
            setError("root", {
              message:
                "This link has expired. Please request for a new password reset link.",
            });
          } else if (
            err.response?.data.error === PasswordResetErrors.INVALID_TOKEN
          ) {
            setError("root", {
              message:
                "This password reset link is invalid. Please request for a new link.",
            });
          } else {
            setError("root", {
              message: "An unknown error occured. Please try again later.",
            });
          }
        } else {
          setError("root", {
            message: "An unknown error occured. Please try again later.",
          });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <div className="w-80">
      <div className="text-[#333] font-semibold text-2xl mb-6">
        Change Your Password
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="w-full">
          <div className="mt-4">
            <Label htmlFor="password" className="text-xs">
              NEW PASSWORD
            </Label>
            <Input
              type="password"
              id="password"
              className="rounded-xs"
              {...register("password", {
                required: "Password is required.",
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters long.",
                },
                maxLength: {
                  value: 32,
                  message: "Password can be a maximum of 32 characters long.",
                },
              })}
            />
            {errors.password && (
              <p className="text-[#e30000] text-xs mt-1">
                {errors.password?.message}
              </p>
            )}
          </div>
          <Button
            type="submit"
            className="mt-4 w-full rounded-sm"
            disabled={submitting}
          >
            {submitting ? (
              <Loader2 className="animate-spin" />
            ) : (
              "Change Password"
            )}
          </Button>
          {errors.root && (
            <p className="text-[#e30000] text-xs mt-1">{errors.root.message}</p>
          )}
        </div>
      </form>
    </div>
  );
};
